import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Clock, Users, ChefHat, Bookmark, BookmarkCheck, Printer, Share2, ChevronLeft, Check, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../App';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { LogoSmall } from '../components/Logo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RecipeDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [checkedIngredients, setCheckedIngredients] = useState([]);
  const [relatedRecipes, setRelatedRecipes] = useState([]);

  useEffect(() => {
    fetchRecipe();
  }, [slug]);

  const fetchRecipe = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/recipes/slug/${slug}`);
      setRecipe(res.data);
      
      // Fetch related recipes
      if (res.data.categories && res.data.categories.length > 0) {
        const relatedRes = await axios.get(`${API_URL}/api/recipes?category=${res.data.categories[0]}&limit=4`);
        setRelatedRecipes((relatedRes.data.recipes || []).filter(r => r.id !== res.data.id).slice(0, 3));
      }
      
      // Check if saved
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const savedRes = await axios.get(`${API_URL}/api/recipes/${res.data.id}/is-saved`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setIsSaved(savedRes.data.saved);
        } catch (e) {
          // Not logged in or error
        }
      }
    } catch (e) {
      console.error('Error fetching recipe:', e);
      navigate('/recipes');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRecipe = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please sign in to save recipes');
      return;
    }

    try {
      if (isSaved) {
        await axios.delete(`${API_URL}/api/recipes/${recipe.id}/save`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsSaved(false);
        toast.success('Recipe removed from saved');
      } else {
        await axios.post(`${API_URL}/api/recipes/${recipe.id}/save`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsSaved(true);
        toast.success('Recipe saved!');
      }
    } catch (e) {
      toast.error('Failed to save recipe');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: recipe.title,
          text: recipe.description,
          url: window.location.href
        });
      } catch (e) {
        // User cancelled
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  const toggleIngredient = (index) => {
    setCheckedIngredients(prev => 
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  const totalTime = recipe ? (recipe.prep_time || 0) + (recipe.rehydration_time || 0) + (recipe.cook_time || 0) : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#003456] border-t-transparent"></div>
      </div>
    );
  }

  if (!recipe) return null;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />

      {/* Hero Image */}
      {recipe.featured_image && (
        <div className="pt-16 relative h-[40vh] md:h-[50vh]">
          <img 
            src={recipe.featured_image} 
            alt={recipe.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        </div>
      )}

      <div className={`${recipe.featured_image ? '-mt-24 relative z-10' : 'pt-44'} pb-16`}>
        <div className="max-w-4xl mx-auto px-6">
          {/* Recipe Card */}
          <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
            {/* Header */}
            <div className="p-8 md:p-12">
              <Link to="/recipes" className="inline-flex items-center text-[#003456] hover:text-[#002B47] mb-6 font-medium">
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back to Recipes
              </Link>

              {/* Categories */}
              {recipe.categories && recipe.categories.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {recipe.categories.map(cat => (
                    <Link 
                      key={cat} 
                      to={`/recipes?category=${cat}`}
                      className="px-3 py-1 bg-[#dbeef5] text-[#003456] text-sm font-medium rounded-full capitalize hover:bg-[#003456] hover:text-white transition-colors"
                    >
                      {cat}
                    </Link>
                  ))}
                </div>
              )}

              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0a0203] mb-4" style={{  }}>
                {recipe.title}
              </h1>

              <p className="text-lg text-gray-600 mb-6">{recipe.description}</p>

              {/* Meta Info */}
              <div className="flex flex-wrap items-center gap-6 mb-6 text-gray-600">
                {totalTime > 0 && (
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-[#003456]" />
                    <div>
                      <p className="font-semibold text-[#0a0203]">{totalTime} min</p>
                      <p className="text-xs">Total Time</p>
                    </div>
                  </div>
                )}
                {recipe.servings && (
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-[#003456]" />
                    <div>
                      <p className="font-semibold text-[#0a0203]">{recipe.servings}</p>
                      <p className="text-xs">Servings</p>
                    </div>
                  </div>
                )}
                {recipe.difficulty && (
                  <div className="flex items-center gap-2">
                    <ChefHat className="h-5 w-5 text-[#003456]" />
                    <div>
                      <p className="font-semibold text-[#0a0203] capitalize">{recipe.difficulty}</p>
                      <p className="text-xs">Difficulty</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Time Breakdown */}
              {(recipe.prep_time || recipe.rehydration_time || recipe.cook_time) && (
                <div className="flex flex-wrap gap-4 mb-6 p-4 bg-[#F8FAFC] rounded-xl">
                  {recipe.prep_time > 0 && (
                    <div className="text-center">
                      <p className="text-sm text-gray-500">Prep</p>
                      <p className="font-semibold">{recipe.prep_time} min</p>
                    </div>
                  )}
                  {recipe.rehydration_time > 0 && (
                    <div className="text-center">
                      <p className="text-sm text-gray-500">Rehydration</p>
                      <p className="font-semibold">{recipe.rehydration_time} min</p>
                    </div>
                  )}
                  {recipe.cook_time > 0 && (
                    <div className="text-center">
                      <p className="text-sm text-gray-500">Cook</p>
                      <p className="font-semibold">{recipe.cook_time} min</p>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-3">
                <Button 
                  onClick={handleSaveRecipe}
                  variant={isSaved ? "default" : "outline"}
                  className={isSaved ? "bg-[#003456]" : ""}
                  data-testid="save-recipe-btn"
                >
                  {isSaved ? <BookmarkCheck className="h-4 w-4 mr-2" /> : <Bookmark className="h-4 w-4 mr-2" />}
                  {isSaved ? 'Saved' : 'Save Recipe'}
                </Button>
                <Button variant="outline" onClick={handlePrint}>
                  <Printer className="h-4 w-4 mr-2" />
                  Print
                </Button>
                <Button variant="outline" onClick={handleShare}>
                  <Share2 className="h-4 w-4 mr-2" />
                  Share
                </Button>
              </div>
            </div>

            {/* Content Grid */}
            <div className="grid md:grid-cols-3 gap-0 border-t border-gray-100">
              {/* Ingredients */}
              <div className="p-8 md:border-r border-gray-100">
                <h2 className="text-xl font-bold text-[#0a0203] mb-4" style={{  }}>
                  Ingredients
                </h2>
                {recipe.ingredients_html ? (
                  <div 
                    className="prose prose-sm max-w-none recipe-content"
                    dangerouslySetInnerHTML={{ __html: recipe.ingredients_html }}
                  />
                ) : recipe.ingredients && recipe.ingredients.length > 0 ? (
                  <ul className="space-y-3">
                    {recipe.ingredients.map((ing, index) => (
                      <li 
                        key={index}
                        onClick={() => toggleIngredient(index)}
                        className={`flex items-start gap-3 cursor-pointer transition-colors ${checkedIngredients.includes(index) ? 'text-gray-400 line-through' : ''}`}
                      >
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${checkedIngredients.includes(index) ? 'bg-[#003456] border-[#003456]' : 'border-gray-300'}`}>
                          {checkedIngredients.includes(index) && <Check className="h-3 w-3 text-white" />}
                        </div>
                        <span>
                          <strong>{ing.amount}{ing.unit ? ` ${ing.unit}` : ''}</strong> {ing.name}
                          {ing.notes && <span className="text-gray-500 text-sm"> ({ing.notes})</span>}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500">No ingredients listed.</p>
                )}
              </div>

              {/* Instructions */}
              <div className="md:col-span-2 p-8">
                <h2 className="text-xl font-bold text-[#0a0203] mb-4" style={{  }}>
                  Instructions
                </h2>
                {recipe.instructions_html ? (
                  <div 
                    className="prose prose-sm max-w-none recipe-content"
                    dangerouslySetInnerHTML={{ __html: recipe.instructions_html }}
                  />
                ) : recipe.instructions && recipe.instructions.length > 0 ? (
                  <ol className="space-y-6">
                    {recipe.instructions.map((step, index) => (
                      <li key={index} className="flex gap-4">
                        <div className="w-8 h-8 bg-[#003456] text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                          {index + 1}
                        </div>
                        <p className="text-gray-700 pt-1">{step}</p>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p className="text-gray-500">No instructions available.</p>
                )}

                {/* Tips */}
                {recipe.tips && recipe.tips.length > 0 && (
                  <div className="mt-8 p-6 bg-[#FFF8E1] rounded-xl">
                    <h3 className="font-bold text-[#0a0203] mb-3 flex items-center gap-2">
                      <Info className="h-5 w-5 text-yellow-600" />
                      Pro Tips
                    </h3>
                    <ul className="space-y-2">
                      {recipe.tips.map((tip, index) => (
                        <li key={index} className="text-gray-700 text-sm flex items-start gap-2">
                          <span className="text-yellow-600">•</span>
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Nutrition */}
            {recipe.nutrition && Object.keys(recipe.nutrition).length > 0 && (
              <div className="p-8 border-t border-gray-100">
                <h2 className="text-xl font-bold text-[#0a0203] mb-4" style={{  }}>
                  Nutrition (per serving)
                </h2>
                <div className="flex flex-wrap gap-6">
                  {recipe.nutrition.calories && (
                    <div className="text-center">
                      <p className="text-2xl font-bold text-[#003456]">{recipe.nutrition.calories}</p>
                      <p className="text-sm text-gray-500">Calories</p>
                    </div>
                  )}
                  {recipe.nutrition.protein && (
                    <div className="text-center">
                      <p className="text-2xl font-bold text-[#003456]">{recipe.nutrition.protein}g</p>
                      <p className="text-sm text-gray-500">Protein</p>
                    </div>
                  )}
                  {recipe.nutrition.carbs && (
                    <div className="text-center">
                      <p className="text-2xl font-bold text-[#003456]">{recipe.nutrition.carbs}g</p>
                      <p className="text-sm text-gray-500">Carbs</p>
                    </div>
                  )}
                  {recipe.nutrition.fat && (
                    <div className="text-center">
                      <p className="text-2xl font-bold text-[#003456]">{recipe.nutrition.fat}g</p>
                      <p className="text-sm text-gray-500">Fat</p>
                    </div>
                  )}
                  {recipe.nutrition.fiber && (
                    <div className="text-center">
                      <p className="text-2xl font-bold text-[#003456]">{recipe.nutrition.fiber}g</p>
                      <p className="text-sm text-gray-500">Fiber</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Tags */}
            {recipe.tags && recipe.tags.length > 0 && (
              <div className="p-8 border-t border-gray-100">
                <div className="flex flex-wrap gap-2">
                  {recipe.tags.map(tag => (
                    <Link 
                      key={tag} 
                      to={`/recipes?tag=${encodeURIComponent(tag)}`}
                      className="px-4 py-2 bg-gray-100 text-gray-600 text-sm rounded-full hover:bg-[#dbeef5] hover:text-[#003456] transition-colors"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Related Recipes */}
          {relatedRecipes.length > 0 && (
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-[#0a0203] mb-6" style={{  }}>
                More Recipes You'll Love
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                {relatedRecipes.map(r => (
                  <Link key={r.id} to={`/recipes/${r.slug}`} className="group">
                    <article className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all">
                      <div className="aspect-[4/3] overflow-hidden">
                        <img 
                          src={r.featured_image || 'https://images.unsplash.com/photo-1606787366850-de6330128bfc?w=600'} 
                          alt={r.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="font-semibold text-[#0a0203] group-hover:text-[#003456] transition-colors line-clamp-2">
                          {r.title}
                        </h3>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#0a0203] text-white py-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <LogoSmall />
            <span className="text-xl font-bold" style={{  }}>D-Freeze</span>
          </div>
          <p className="text-gray-400 text-sm">
            &copy; {new Date().getFullYear()} D-Freeze. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default RecipeDetailPage;
